import store from '@/store';
import LocalStorage from './LocalStorage';
import SessionStorage from './SessionStorage';
import CookieStorage from './CookieStorage';

const COOKIE_DAYS = 3650;

function isUsableValue(v) {
  if (v === undefined || v === null) return false;
  if (v === '' || v === 'undefined' || v === 'null') return false;
  return true;
}

// tizen.keymanager ist eine B2B/Hospitality-API. Auf Consumer-TVs (z.B. BU8000)
// fehlt sie, jeder direkte Aufruf wirft eine TypeError-Exception und ein
// console.log auf TV-WebViews ist teuer. Einmalige Detection beim ersten
// Aufruf, danach gecachet.
let keymanagerStatus = null;
function hasKeymanager() {
  if (keymanagerStatus !== null) return keymanagerStatus;
  try {
    keymanagerStatus = !!(
      typeof tizen !== 'undefined' &&
      tizen &&
      tizen.keymanager &&
      typeof tizen.keymanager.getData === 'function' &&
      typeof tizen.keymanager.saveData === 'function'
    );
  } catch (e) {
    keymanagerStatus = false;
  }
  console.log(`[TizenKeyStorage] keymanager available: ${keymanagerStatus}`);
  return keymanagerStatus;
}

export default class TizenKeyStorage {
  static get(name) {
    // 1. Tizen keymanager (primary) - nur wenn API verfuegbar.
    if (hasKeymanager()) {
      let value;
      try {
        value = tizen.keymanager.getData({ name });
      } catch (error) {
        console.log(
          `[TizenKeyStorage] keymanager.getData failed for "${name}": ${error.name} - ${error.message}`
        );
      }
      if (isUsableValue(value)) return value;
    }

    // 2-4. Fallback-Kette: localStorage -> Cookie -> sessionStorage.
    const fallbacks = [
      ['localStorage',   () => LocalStorage.get(name)],
      ['cookie',         () => CookieStorage.get(name)],
      ['sessionStorage', () => SessionStorage.get(name)],
    ];
    for (const [source, getter] of fallbacks) {
      let v;
      try { v = getter(); } catch (e) { continue; }
      if (isUsableValue(v)) {
        console.log(`[TizenKeyStorage] re-hydrating "${name}" from ${source}`);
        this._restore(name, v);
        return v;
      }
    }
    return undefined;
  }

  static save(name, value) {
    // Sofort in alle ergaenzenden Speicher spiegeln (sync, schnell).
    this._mirror(name, value);
    if (!hasKeymanager()) return Promise.resolve();
    return new Promise((resolve) => {
      const callback = () => resolve();
      try {
        this._removeFromKeymanager(name);
        tizen.keymanager.saveData(name, value, null, callback, callback);
      } catch (error) {
        store.dispatch('notification/new', {
          title: 'Save data ' + name,
          message: value,
        });
        resolve();
      }
    });
  }

  static remove(name) {
    try { LocalStorage.remove(name); } catch (e) {}
    try { SessionStorage.remove(name); } catch (e) {}
    try { CookieStorage.remove(name); } catch (e) {}
    if (hasKeymanager()) this._removeFromKeymanager(name);
  }

  static _mirror(name, value) {
    try { LocalStorage.save(name, value); } catch (e) {}
    try { SessionStorage.save(name, value); } catch (e) {}
    try { CookieStorage.save(name, value, COOKIE_DAYS); } catch (e) {}
  }

  // Wert aus Fallback gefunden -> sync in die anderen WebStorages spiegeln,
  // keymanager-Restore async ins Naechste-Tick deferren, damit der Read-Pfad
  // nicht durch einen evtl. langsamen keymanager.saveData blockiert wird.
  static _restore(name, value) {
    this._mirror(name, value);
    if (!hasKeymanager()) return;
    setTimeout(() => {
      try {
        tizen.keymanager.saveData(name, value, null, () => {}, () => {});
      } catch (e) {}
    }, 0);
  }

  static _removeFromKeymanager(name) {
    try {
      tizen.keymanager.removeData({ name });
    } catch (error) {
      console.log(`[TizenKeyStorage] keymanager.removeData failed for "${name}":`, error);
    }
  }
}



// WEBPACK FOOTER //
// ./src/helpers/StorageHandler/Tizen.js